/**
 * Root route branch (Story 20.1)
 *
 * `/` serves two audiences (AD-1): anonymous visitors get the public landing
 * (PublicHomePage); authenticated users get the existing dashboard (HomePage).
 *
 * While auth state is still resolving (`isLoading`), render nothing — NOT the
 * public page — so an authenticated user reloading `/` never flashes the public
 * landing before the session restores.
 *
 * IMPORTANT: do not branch on the persisted `isAuthenticated` flag before
 * `isLoading` settles. The zustand auth store `partialize`s `isAuthenticated`
 * to localStorage, so on reload it rehydrates `true` before onAuthStateChanged
 * resolves — gating on `isLoading` first prevents a stale-auth dashboard flash.
 *
 * The dashboard HomePage stays lazy so anonymous visitors never download the
 * dashboard chunk. PublicHomePage is EAGER-imported (S20.2 AC10): it is the
 * anonymous LCP/conversion surface, so it must not pay an extra chunk fetch.
 * They render inside App.tsx's existing <Suspense> boundary.
 *
 * @module components/routing/RootRoute
 */

import { lazy } from 'react';
import { useIsAuthenticated, useAuthLoading } from '../../stores/auth-store';
import { PublicHomePage } from '../../pages/PublicHomePage';

const HomePage = lazy(() => import('../../pages/HomePage').then((m) => ({ default: m.HomePage })));

export function RootRoute(): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();

  // Auth still resolving — render nothing to avoid a flash of the public page
  // for a returning authenticated user.
  if (isLoading) {
    return <></>;
  }

  return isAuthenticated ? <HomePage /> : <PublicHomePage />;
}
